import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { dateRangeValidator } from './dateRangeValidator';

export type WeeklyReportRegenerationTargetType = 'category' | 'plan';

export interface WeeklyReportRegenerationDialogData {
  targetType: WeeklyReportRegenerationTargetType;
  title: string;
  scopeLabel: string;
  defaultStartDate?: string | null;
  defaultEndDate?: string | null;
  allowTeacherTraining?: boolean;
  defaultIncludeTeacherTraining?: boolean;
}

export interface WeeklyReportRegenerationDialogResult {
  start_date: string;
  end_date: string;
  force: boolean;
  include_teacher_training?: boolean;
}

@Component({
  selector: 'app-weekly-report-regeneration-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatCheckboxModule, MatRadioModule],
  templateUrl: './weekly-report-regeneration-dialog.component.html',
  styleUrls: ['./weekly-report-regeneration-dialog.component.scss'],
})
export class WeeklyReportRegenerationDialogComponent {
  readonly dialogRef = inject(MatDialogRef<WeeklyReportRegenerationDialogComponent>);
  readonly data = inject<WeeklyReportRegenerationDialogData>(MAT_DIALOG_DATA);

  readonly form = new FormGroup(
    {
      start_date: new FormControl(this.data.defaultStartDate ?? '', Validators.required),
      end_date: new FormControl(this.data.defaultEndDate ?? '', Validators.required),
      force: new FormControl<boolean>(false, { nonNullable: true }),
      include_teacher_training: new FormControl<boolean>(this.data.defaultIncludeTeacherTraining ?? false, {
        nonNullable: true,
      }),
    },
    { validators: dateRangeValidator }
  );

  constructor() {
    this.form.patchValue({
      force: false,
      include_teacher_training: this.data.defaultIncludeTeacherTraining ?? false,
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const rawValue = this.form.getRawValue();
    const result: WeeklyReportRegenerationDialogResult = {
      start_date: rawValue.start_date ?? '',
      end_date: rawValue.end_date ?? '',
      force: !!rawValue.force,
    };

    if (this.data.allowTeacherTraining && rawValue.include_teacher_training) {
      result.include_teacher_training = true;
    }

    this.dialogRef.close(result);
  }
}